<template>
  <Teleport to="body">
    <div
      v-if="modelValue"
      class="modal-backdrop"
      @click.self="onClose"
    >
      <div
        class="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dup-title"
      >
        <header class="modal__header">
          <h2
            id="dup-title"
            class="modal__title"
          >
            Angebot duplizieren
          </h2>
          <button
            class="icon-btn"
            aria-label="Schließen"
            @click="onClose"
          >
            ×
          </button>
        </header>

        <section class="modal__body">
          <div
            v-if="error || (submitted && errorList.length)"
            class="alert alert--error"
          >
            <p v-if="submitted && errorList.length">
              Bitte korrigiere die markierten Felder.
            </p>
            <p v-if="error">
              {{ error }}
            </p>
            <ul v-if="submitted && errorList.length">
              <li
                v-for="(msg, i) in errorList"
                :key="i"
              >
                {{ msg }}
              </li>
            </ul>
          </div>

          <!-- Angebot/Thema -->
          <div class="row">
            <div class="col">
              <label
                for="f-subject"
                class="label"
              >Angebot / Fach</label>
              <input
                id="f-subject"
                v-model="subjectInput"
                type="text"
                :class="['input', {'is-invalid': submitted && !!errors.subject}]"
                placeholder="z. B. Mathematik"
                list="subjects-list"
                autocomplete="off"
                @input="onSubjectInput"
              >
              <datalist id="subjects-list">
                <option
                  v-for="s in subjectOptions"
                  :key="`subj-${s}`"
                  :value="s"
                />
              </datalist>
              <small class="hint">Tippe zum Suchen. Neue Angebote können direkt eingetippt werden.</small>
            </div>
            <div class="col">
              <label
                for="f-topic"
                class="label"
              >Thema</label>
              <input
                id="f-topic"
                v-model.trim="form.topic"
                type="text"
                :class="['input', {'is-invalid': submitted && !!errors.topic}]"
                placeholder="z. B. Lineare Funktionen"
                autocomplete="off"
              >
              <small class="hint">&nbsp;</small>
            </div>
          </div>

          <!-- Zeit/Ort -->
          <div class="row">
            <div class="col pl-0">
              <div class="row row--wrap">
                <div class="col col--datetime">
                  <label
                    for="f-start-dt"
                    class="label"
                  >Start</label>
                  <div
                    class="control"
                    :class="{'is-invalid': submitted && !!errors.startsAt}"
                  >
                    <FlatPickr
                      id="f-start-dt"
                      ref="startPicker"
                      v-model="form.startsAt"
                      :config="startConfig"
                      class="input flatpickr-datetime"
                      placeholder="TT.MM.JJJJ HH:MM"
                    />
                  </div>
                  <small class="hint">&nbsp;</small>
                </div>
                <div class="col col--datetime">
                  <label
                    for="f-end-dt"
                    class="label"
                  >Ende</label>
                  <div
                    class="control"
                    :class="{'is-invalid': submitted && !!errors.endsAt}"
                  >
                    <FlatPickr
                      id="f-end-dt"
                      ref="endPicker"
                      v-model="form.endsAt"
                      :config="endConfig"
                      class="input flatpickr-datetime"
                      placeholder="TT.MM.JJJJ HH:MM"
                    />
                  </div>
                  <small class="hint">&nbsp;</small>
                </div>
              </div>
            </div>
            <div class="col">
              <div class="row row--single">
                <div class="col px-0">
                  <label
                    for="f-location"
                    class="label"
                  >Ort</label>
                  <input
                    id="f-location"
                    v-model="locationInput"
                    type="text"
                    :class="['input', {'is-invalid': submitted && !!errors.locationName}]"
                    placeholder="z.B. Schule oder Schwimmhalle Baff"
                    list="locations-list"
                    autocomplete="off"
                    @input="onLocationInput"
                  >
                  <datalist id="locations-list">
                    <option
                      v-for="l in locationOptions"
                      :key="`loc-${l}`"
                      :value="l"
                    />
                  </datalist>
                  <small class="hint">Tippe zum Suchen. Neue Orte einfach eintragen.</small>
                </div>
              </div>
            </div>
          </div>

          <!-- Status/Mandatory/Teacher -->
          <div class="row">
            <div class="col pl-0">
              <div class="row row--wrap">
                <div class="col col--datetime">
                  <label
                    for="f-status"
                    class="label"
                  >Status</label>
                  <select
                    id="f-status"
                    v-model="form.status"
                    class="input"
                  >
                    <option value="planned">
                      in Vorbereitung
                    </option>
                  </select>
                </div>
                <div class="col col--checkbox col--datetime">
                  <label class="label">&nbsp;</label>
                  <label class="checkbox">
                    <input
                      v-model="form.isMandatory"
                      type="checkbox"
                    >
                    <span>Pflichtangebot</span>
                  </label>
                </div>
              </div>
            </div>
            <div
              v-if="isAdmin"
              class="col"
            >
              <div class="row row--single">
                <div class="col px-0">
                  <label
                    for="f-teacher"
                    class="label"
                  >Verantwortlich</label>
                  <select
                    id="f-teacher"
                    v-model="form.teacherId"
                    class="input"
                  >
                    <option
                      v-for="u in responsibleOptionsLabeled"
                      :key="u.id"
                      :value="u.id"
                    >
                      {{ u.label }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </section>

        <footer class="modal__footer">
          <button
            class="btn btn-ghost"
            type="button"
            @click="onClose"
          >
            Abbrechen
          </button>
          <button
            class="btn btn-primary"
            type="button"
            @click="save"
          >
            {{ saving ? 'Speichern…' : 'Speichern' }}
          </button>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import {ref, reactive, computed, onMounted, watch} from 'vue'
import FlatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import {German} from 'flatpickr/dist/l10n/de.js'
import api from '@/services/api'
import {useAuthStore} from '@/stores/auth'
import {useToast} from 'primevue/usetoast'
import {getSubjectsOnce, getLocationsOnce, getUsersOnce} from '@/services/catalogsService'

const auth = useAuthStore()
const isAdmin = computed(() => auth.user?.roleId === 1)
const currentUserId = computed(() => auth.user?.id ?? null)
const toast = useToast()

const props = defineProps({
  modelValue: {type: Boolean, default: false},
  event: {type: Object, default: () => ({})},
  subjects: {type: Array, default: () => []},
  locations: {type: Array, default: () => []}
})
const emit = defineEmits(['update:modelValue', 'saved'])

const form = reactive({
  topic: '',
  subject: '',
  startsAt: /** @type {Date|string|null} */(null),
  endsAt:   /** @type {Date|string|null} */(null),
  locationName: '',
  status: 'planned',
  isMandatory: false,
  teacherId: null
})

const dtConfigBase = {
  enableTime: true,
  dateFormat: 'Y-m-d H:i',
  altInput: true,
  altFormat: 'd.m.Y H:i',
  time_24hr: true,
  allowInput: true,
  clickOpens: true,
  disableMobile: true,
  locale: German
}
const startConfig = {...dtConfigBase}
const endConfig = reactive({...dtConfigBase, minDate: null})

const startPicker = ref(null)
const endPicker = ref(null)

const error = ref('')
const submitted = ref(false)
const errors = reactive({subject: '', topic: '', startsAt: '', endsAt: '', teacherId: '', locationName: ''})
const errorList = computed(() => Object.values(errors).filter(Boolean))
const saving = ref(false)

const subjectInput = ref('')
const subjectOptions = ref([...props.subjects])

function onSubjectInput(e) {
  form.subject = e?.target?.value ?? ''
}

const locationInput = ref('')
const locationOptions = ref([...props.locations])

function onLocationInput(e) {
  form.locationName = e?.target?.value ?? ''
}

function asDate(val) {
  if (!val) return null
  if (val instanceof Date) {
    return Number.isNaN(val.getTime()) ? null : val
  }
  if (typeof val === 'string') {
    if (/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}$/.test(val)) {
      const [d, t] = val.split(' ')
      const [y, m, da] = d.split('-').map(Number)
      const [hh, mm] = t.split(':').map(Number)
      return new Date(y, m - 1, da, hh, mm, 0, 0)
    }
    if (/^\d{2}\.\d{2}\.\d{4}\s\d{2}:\d{2}$/.test(val)) {
      const [d, t] = val.split(' ')
      const [da, m, y] = d.split('.').map(Number)
      const [hh, mm] = t.split(':').map(Number)
      return new Date(y, m - 1, da, hh, mm, 0, 0)
    }
    const iso = new Date(val)
    return Number.isNaN(iso.getTime()) ? null : iso
  }
  return null
}

function toIso(dt) {
  const d = asDate(dt)
  return d ? d.toISOString() : null
}

function clearErrors() {
  errors.subject = ''
  errors.topic = ''
  errors.startsAt = ''
  errors.endsAt = ''
  errors.teacherId = ''
  errors.locationName = ''
}

function validateFields() {
  clearErrors()
  let ok = true
  if (!form.subject?.trim()) {
    errors.subject = 'Bitte Angebot / Fach ausfüllen.';
    ok = false
  }
  if (!form.topic?.trim()) {
    errors.topic = 'Bitte Thema ausfüllen.';
    ok = false
  }
  const startDt = asDate(form.startsAt)
  const endDt = asDate(form.endsAt)
  if (!startDt) {
    errors.startsAt = 'Bitte Start (Datum & Zeit) wählen.';
    ok = false
  }
  if (!endDt) {
    errors.endsAt = 'Bitte Ende (Datum & Zeit) wählen.';
    ok = false
  }
  if (startDt && endDt && endDt <= startDt) {
    errors.endsAt = 'Ende muss nach dem Start liegen.';
    ok = false
  }
  if (!form.locationName?.trim()) {
    errors.locationName = 'Bitte Ort ausfüllen.';
    ok = false
  }
  if (isAdmin.value && !form.teacherId) {
    errors.teacherId = 'Bitte Verantwortlichen auswählen.';
    ok = false
  }
  return ok
}

function reset() {
  form.topic = ''
  form.subject = ''
  form.startsAt = null
  form.endsAt = null
  form.locationName = ''
  form.status = 'planned'
  form.isMandatory = false
  form.teacherId = currentUserId.value || null
  submitted.value = false
  clearErrors()
  error.value = ''
}

onMounted(async () => {
  if (!props.subjects?.length) {
    try {
      subjectOptions.value = await getSubjectsOnce(api)
    } catch {
      /* ignore */
    }
  } else {
    subjectOptions.value = [...props.subjects]
  }

  if (!props.locations?.length) {
    try {
      locationOptions.value = await getLocationsOnce(api)
    } catch {
      /* ignore */
    }
  } else {
    locationOptions.value = [...props.locations]
  }

  await ensureResponsibleLoaded()
})

const responsibleOptions = ref([])
const responsibleOptionsLabeled = computed(() => {
  return (responsibleOptions.value || []).map(u => {
    const roleId = u.roleId ?? u.role_id
    const role = roleId === 1 ? 'Admin' : 'Lehrer'
    return {
      id: u.id,
      label: `${u.displayName} (${role})`,
      roleId
    }
  })
})

async function ensureResponsibleLoaded() {
  if (!isAdmin.value) return
  if (responsibleOptions.value.length > 0) return
  try {
    const list = await getUsersOnce(api)
    responsibleOptions.value = (list || []).filter(u => (u.roleId ?? u.role_id) === 1 || (u.roleId ?? u.role_id) === 2)
  } catch {
    /* ignore */
  }
}

/* Prefill beim Öffnen (Werte aus übergebenem Event) */
watch(() => props.modelValue, async (open) => {
  if (!open) return
  submitted.value = false
  clearErrors()

  // Sicherstellen, dass Verantwortliche geladen sind (nur für Admins)
  if (isAdmin.value) {
    await ensureResponsibleLoaded()
  }

  const e = props.event || {}
  form.topic = ''
  form.subject = e.subject || ''
  form.startsAt = e.starts_at || e.startsAt || null
  form.endsAt = e.ends_at || e.endsAt || null
  form.locationName = e.location || ''
  form.status = 'planned'
  form.isMandatory = Boolean(e.isMandatory ?? e.is_mandatory ?? false)
  form.teacherId = e.teacher_id || e.teacherId || (currentUserId.value || null)

  const start = asDate(form.startsAt)
  const end = asDate(form.endsAt)
  if (endPicker.value?.fp) endPicker.value.fp.set('minDate', start || null)
  if (!end || (start && end <= start)) {
    form.endsAt = start ? new Date(start.getTime() + 45 * 60 * 1000) : null
  }

  subjectInput.value = form.subject || ''
  locationInput.value = form.locationName || ''
})

/* Live-Validierung & Start→Ende-Kopplung */
watch(() => form.startsAt, (dt) => {
  const start = asDate(dt)
  if (endPicker.value?.fp) endPicker.value.fp.set('minDate', start || null)
  if (submitted.value) {
    errors.startsAt = start ? '' : 'Bitte Start (Datum & Zeit) wählen.'
  }
  const end = asDate(form.endsAt)
  if (start && (!end || end <= start)) {
    form.endsAt = new Date(start.getTime() + 45 * 60 * 1000)
  }
})
watch(() => form.endsAt, (dt) => {
  if (!submitted.value) return
  const end = asDate(dt)
  if (!end) {
    errors.endsAt = 'Bitte Ende (Datum & Zeit) wählen.'
    return
  }
  const start = asDate(form.startsAt)
  errors.endsAt = (start && end <= start) ? 'Ende muss nach dem Start liegen.' : ''
})
watch(() => form.subject, (v) => {
  if (submitted.value && v?.trim()) errors.subject = ''
})
watch(() => form.topic, (v) => {
  if (submitted.value && v?.trim()) errors.topic = ''
})
watch(() => form.locationName, (v) => {
  if (submitted.value && v?.trim()) errors.locationName = ''
})
watch(() => form.teacherId, (v) => {
  if (submitted.value && (!!v || !isAdmin.value)) errors.teacherId = ''
})

async function save() {
  error.value = ''
  submitted.value = true
  const ok = validateFields()
  if (!ok) return
  saving.value = true
  try {
    const payload = {
      topic: form.topic?.trim() || null,
      subject: form.subject?.trim() || null,
      startsAt: toIso(form.startsAt),
      endsAt: toIso(form.endsAt),
      location: form.locationName?.trim() || null,
      status: form.status,
      isMandatory: !!form.isMandatory,
      teacherId: form.teacherId ?? null
    }
    const srcId = Number(props.event?.id || 0)
    const res = await api.post(`/events/${encodeURIComponent(srcId)}/duplicate`, payload)
    const created = res?.data
    const data = created?.data ?? created
    if (data && data.id) {
      emit('saved', data)
      toast.add({severity: 'success', summary: 'Angebot dupliziert.', life: 3500})
      onClose(true)
    } else {
      error.value = 'Konnte nicht gespeichert werden.'
    }
  } catch (e) {
    error.value = e?.response?.data?.message || 'Fehler beim Speichern.'
  } finally {
    saving.value = false
  }
}

function onClose(success = false) {
  emit('update:modelValue', false)
  if (!success) reset()
}
</script>
<style scoped>
/* Layout DuplicateEventDialog.vue */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, .55);
  display: grid;
  place-items: center;
  z-index: 1000;
}

.modal {
  width: min(980px, 94vw);
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(2, 6, 23, .25);
  display: grid;
  grid-template-rows: auto 1fr auto;
  max-height: 92vh;
}

.modal__header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.2rem;
  border-bottom: 1px solid #e5e7eb;
}

.modal__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #0f172a;
}

.icon-btn {
  border: 0;
  background: transparent;
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  color: #334155;
}

.modal__body {
  padding: 1rem 1.2rem;
  overflow: auto;
}

.modal__footer {
  padding: .9rem 1.2rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: .6rem;
}

.row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: .9rem 1rem;
}

.row--wrap {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: .6rem;
}

.row--single {
  grid-template-columns: 1fr;
}

.col {
  display: grid;
  gap: .35rem;
}

.col--datetime {
  min-width: 14rem;
  padding-right: 0;
}

.flatpickr-datetime :deep(.flatpickr-input) {
  width: 100%;
  padding: .55rem .7rem;
  border: 1px solid #d0d5dd;
  border-radius: .55rem;
  font-size: .95rem;
  background: #fff;
  height: calc(2.2rem);
  line-height: 1.2rem;
}

:deep(input.flatpickr-datetime) {
  box-sizing: border-box;
  height: 38px;
  padding: .45rem .65rem;
  border: 1px solid #d0d5dd;
  border-radius: .55rem;
  line-height: 1.25;
  font-size: .95rem;
  background: #fff;
  width: 100%;
}

.input, select {
  width: 100%;
  padding: .55rem .7rem;
  border: 1px solid #d0d5dd;
  border-radius: .55rem;
  font-size: .95rem;
  background: #fff;
}

.input.is-invalid, select.is-invalid {
  border-color: #ef4444 !important;
  box-shadow: 0 0 0 1px rgba(239, 68, 68, .08);
}

.control.is-invalid :deep(.flatpickr-input) {
  border-color: #ef4444 !important;
  box-shadow: 0 0 0 1px rgba(239, 68, 68, .08);
}

.is-invalid::placeholder {
  color: #ef4444;
}

.hint {
  color: #6b7280;
  font-size: .8rem;
}

.alert {
  padding: .6rem .8rem;
  border-radius: .55rem;
  margin-bottom: .8rem;
  font-size: .9rem;
}

/* --- Form Controls --- */
.label {
  font-weight: 600;
  font-size: .9rem;
  color: #334155;
}

.alert--error {
  background: #fee2e2;
  color: #7f1d1d;
  border: 1px solid #fecaca;
}

.btn {
  padding: .55rem .9rem;
  border: 1px solid var(--border);
  border-radius: .55rem;
  background: var(--surface);
  color: var(--text);
  cursor: pointer;
}

.btn-ghost {
  background: transparent;
}

.btn:disabled {
  opacity: .65;
  cursor: not-allowed;
}

:root, :host {
  --surface: #f8fafc;
  --text: #0f172a;
  --border: #e5e7eb;
  --primary: #2563eb;
}

.btn-primary {
  background: var(--primary);
  color: #fff;
  border-color: var(--primary);
}

/* --- Responsive --- */
@media (max-width: 1190px) {
  .modal {
    width: min(640px, 100vw);
    max-height: 95vh;
    border-radius: 12px;
  }

  .modal__header,
  .modal__body,
  .modal__footer {
    padding-left: 0.9rem;
    padding-right: 0.9rem;
  }

  .row {
    grid-template-columns: 1fr;
  }

  .row--wrap {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .col--datetime {
    min-width: 0;
  }

  .col {
    gap: 0;
    padding-top: 10px;
    padding-bottom: 0;
  }

  .hint {
    display: none;
  }

  .modal__body > .row > .col {
    padding-top: 0;
  }
}

/* Unter 375px: Eingaben einspaltig */
@media (max-width: 374px) {
  .row--wrap {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .modal {
    width: 100vw;
    border-radius: 0;
  }

  .modal__header,
  .modal__body,
  .modal__footer {
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }
}
</style>